import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingIndicatorComponent } from '@seahorse/ui';
import type { ActionMenuItem } from '@seahorse/ui';
import { SeahorseModule } from '@seahorse/domain';
import { Subscription } from 'rxjs';
import {
  NotificationService,
  ResultStatus,
  RouterUtil,
} from '@seahorse/common';
import {
  DetailHeaderComponent,
  DetailStatus,
  DialogService,
} from '@seahorse/shell';
import { SeahorseModuleService } from '../../services/seahorse-module.service';
import { SeahorseModuleConfigurationCardComponent } from '../../components/seahorse-module-configuration-card/seahorse-module-configuration-card.component';
import { SeahorseModuleOverviewConfigurationCardComponent } from '../../components/seahorse-module-overview-configuration-card/seahorse-module-overview-configuration-card.component';
import { ProxyServices } from '../../../../shared/services/proxy.service';
import { OrganisationStoreService } from '../../../../shared/services/organisation-store.service';

/**
 * Detail page of a Seahorse module (design handoff, "Step 7"):
 * sh-detail-header (back to the overview, status, organisation, delete) and
 * a card per configuration.
 */
@Component({
  selector: 'su-seahorse-module-details',
  templateUrl: './seahorse-module-details.page.html',
  imports: [
    CommonModule,
    TranslateModule,
    RouterModule,
    DetailHeaderComponent,
    LoadingIndicatorComponent,
    SeahorseModuleConfigurationCardComponent,
    SeahorseModuleOverviewConfigurationCardComponent,
  ],
})
export class SeahorseModuleDetailsPage implements OnInit, OnDestroy {
  seahorseModule: SeahorseModule;

  isLoading = false;
  isSaving = false;

  // The header's data is held in fields, set on load (never getters: a new
  // object per change detection would re-render the header endlessly).
  readonly backLink = ['/seahorse-modules/overview'];
  headerTitle = '';
  headerSubline = '';
  headerStatus: DetailStatus | undefined;

  readonly overflowActions: ActionMenuItem[] = [
    {
      label: 'shared.terms.delete',
      icon: 'fa-regular fa-trash',
      danger: true,
      command: () => this.deleteModule(),
    },
  ];

  private _subscriptions = new Subscription();

  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _routerUtil = inject(RouterUtil);
  private _translateService = inject(TranslateService);
  private _proxyService = inject(ProxyServices);
  private _seahorseModuleData = inject(SeahorseModuleService);
  private _notificationService = inject(NotificationService);
  private _dialogService = inject(DialogService);
  private _orgStore = inject(OrganisationStoreService);

  ngOnInit() {
    this._subscriptions.add(
      this._translateService
        .get('seahorseModules.seahorseModule')
        .subscribe((result) => {
          this._routerUtil.currentModuleName = result;
        })
    );

    this.isLoading = true;

    this._subscriptions.add(
      this._route.params.subscribe((routeParams) => {
        const definitionId = routeParams['id'];
        this.getModule(definitionId);
      })
    );
  }

  getModule(id: number) {
    this._subscriptions.add(
      this._seahorseModuleData.getById(id).subscribe((response) => {
        this.seahorseModule = response.result;
        this.loadSeahorseObjects();
      })
    );
  }

  updateModule() {
    this.isSaving = true;

    this._subscriptions.add(
      this._seahorseModuleData.update(this.seahorseModule).subscribe({
        next: (response) => {
          if (response.hasResult) {
            this._notificationService.showSuccess(
              this._translateService.instant('shared.terms.success')
            );
            this.seahorseModule = response.result;
            this.updateHeader();
          }
        },
        error: () => {
          this._notificationService.showError(
            this._translateService.instant('shared.terms.error')
          );
        },
        complete: () => {
          this.isSaving = false;
        },
      })
    );
  }

  deleteModule() {
    this._subscriptions.add(
      this._dialogService
        .confirm({
          title: 'shared.terms.delete',
          message: 'seahorseModules.delete.confirm',
          confirmLabel: 'shared.terms.delete',
          cancelLabel: 'shared.terms.cancel',
          severity: 'danger',
        })
        .subscribe((confirmed) => {
          if (!confirmed) {
            return;
          }

          this._subscriptions.add(
            this._seahorseModuleData
              .delete(this.seahorseModule.id)
              .subscribe((result) => {
                if (result && result.status === ResultStatus.OK) {
                  this._notificationService.showSuccess(
                    this._translateService.instant('shared.terms.success')
                  );
                }

                this._router.navigate(['/seahorse-modules/overview']);
              })
          );
        })
    );
  }

  loadSeahorseObjects() {
    this._proxyService.organisationId = this.seahorseModule.organisationId;
    this._subscriptions.add(
      this._proxyService.getData().subscribe({
        next: () => {
          const name =
            this._proxyService.objectDefinitions.find(
              (item) => item.mappingKey === this.seahorseModule.mappingKey
            )?.name ?? this.seahorseModule.mappingKey;
          this.headerTitle = this._translateService.instant(name);
          this.updateHeader();
        },
        complete: () => {
          this.isLoading = false;
        },
      })
    );
  }

  private updateHeader() {
    this.headerSubline = this._orgStore.getName(
      this.seahorseModule.organisationId
    );
    this.headerStatus = this.seahorseModule.isActive
      ? { label: 'shared.statuses.active', severity: 'success' }
      : { label: 'shared.statuses.inactive', severity: 'danger' };
  }

  ngOnDestroy() {
    this._subscriptions.unsubscribe();
  }
}
